import justifiedLayout from "better-justified-layout";

// True justified rows (filled edge-to-edge), matching the original site. The
// CSS .ratio-photo rules are the no-JS fallback; this enhances them once the
// container width (and image sizes) are known. Grids opt in with
// [data-image-grid]; each photo carries its --aspect-ratio.
function targetRowHeight() {
  const w = window.innerWidth;
  // Phones: small enough that 2-3 photos share a row — one photo per heading
  // reads as a mistake, not a layout.
  if (w < 640) return 120;
  if (w < 768) return 180;
  if (w < 1024) return 200;
  return 240;
}

function layoutGrid(grid) {
  const items = [...grid.children].filter((el) => el.matches('a[style*="--aspect-ratio"]'));
  if (items.length < 2) return;

  const aspectRatios = items.map((item) => {
    const m = (item.getAttribute("style") || "").match(/--aspect-ratio:\s*([0-9.]+)/);
    return m ? parseFloat(m[1]) : 1;
  });

  // Series listing shows a single row (extra photos drop out) — matches the
  // old site's album list.
  const isSingleRow = grid.hasAttribute("data-single-row");
  // Related photos show only complete justified rows (max 2) — the ragged
  // widow row reads as a layout mistake next to the edge-to-edge ones.
  const isFullRows = grid.hasAttribute("data-full-rows");

  const options = {
    containerWidth: grid.clientWidth,
    containerPadding: 0,
    boxSpacing: 0,
    targetRowHeight: targetRowHeight(),
    targetRowHeightTolerance: 0.25,
    ...(isSingleRow ? { maxNumRows: 1 } : {}),
    ...(isFullRows ? { maxNumRows: 2, showWidows: false } : {}),
  };
  let result = justifiedLayout(aspectRatios, options);
  // Too few photos to complete even one row: better a widow row than nothing.
  if (isFullRows && result.boxes.length === 0) {
    result = justifiedLayout(aspectRatios, { ...options, showWidows: true });
  }

  grid.style.position = "relative";
  grid.style.display = "block";
  grid.style.height = result.containerHeight + "px";
  // The CSS pre-JS clamp on [data-single-row] would clip the justified row.
  grid.style.maxHeight = "none";

  items.forEach((item, i) => {
    const box = result.boxes[i];
    if (!box) {
      item.style.display = "none";
      return;
    }
    Object.assign(item.style, {
      display: "",
      position: "absolute",
      left: `${box.left}px`,
      top: `${box.top}px`,
      width: `${box.width}px`,
      height: `${box.height}px`,
      margin: "0",
    });
    // The static sizes attribute assumes full-width tiles; once the real box
    // is known, tell the browser so phones with 3-up rows don't fetch 100vw
    // variants for 130px tiles.
    const img = item.querySelector("img");
    if (img) img.sizes = `${Math.round(box.width)}px`;
  });
}

function applyAll() {
  document.querySelectorAll("[data-image-grid]").forEach(layoutGrid);
}

function debounce(fn, delay) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

// The aspect ratios are server-rendered, so nothing here needs the image
// files — lay out as soon as the DOM is parsed (this is a module script, so
// that's now). Re-run on load in case late content changed the width (a
// scrollbar appearing, say), and on resize.
applyAll();
window.addEventListener("load", applyAll);
window.addEventListener("resize", debounce(applyAll, 150));
